<script lang="ts" setup>
import { useAppointmentSelections } from '@/composables/Appointment/useAppointmentSelections'
import { AppointmentBranch } from '@/type/Appointment'
import { getBranchDisplayNamei18n } from '@/utils/branch'
import { useAppStore } from '@/stores/AppStore'
import { useGeolocation } from '@vueuse/core'
import { useTranslation } from 'i18next-vue'
import { computed, ref } from 'vue'
import { useTheme } from 'vuetify/lib/framework.mjs'

interface Props {
  show: boolean
  branches: AppointmentBranch[]
}
const props = defineProps<Props>()
interface Emits {
  (e: 'close', value: boolean): void
  (e: 'confirm', value: { text: string; uuid: string }): void
}
const emit = defineEmits<Emits>()

const close = () => {
  emit('close', false)
}

const { t } = useTranslation()
const appStore = useAppStore()
const { formatBranchGroup, formatBranchGroupWithRecommended } = useAppointmentSelections()
const { coords, error: geoError } = useGeolocation()

const selected = ref<string | null>(null)
const selectedText = ref<string | null>(null)
const selectBranch = (branch: AppointmentBranch) => {
  const text = getBranchDisplayNamei18n(branch, appStore.language)
  selectedText.value = text
  selected.value = branch.uuid
}
const confirm = () => {
  if (selected.value && selectedText.value) {
    emit('confirm', { text: selectedText.value, uuid: selected.value })
    // close()
  }
}

const theme = useTheme()
const primary2 = theme.current.value.colors?.primary2

const groupingBranch = computed(() => {
  const { latitude, longitude } = coords.value
  if (!geoError.value && isFinite(latitude) && isFinite(longitude)) {
    return formatBranchGroupWithRecommended(props.branches, { latitude, longitude })
  }

  return formatBranchGroup(props.branches)
})

// Debug fields
const overWriteCoords = ref<{ latitude: number; longitude: number } | null>(null)
const selectedCoords = computed(() => {
  const branch = props.branches.find(b => b.uuid === selected.value)
  if (branch) {
    return {
      latitude: branch.map_latitude,
      longitude: branch.map_longitude,
    }
  }
  return null
})
</script>

<template>
  <van-popup :show="show" round position="bottom">
    <div class="tw-p-4 tw-pb-0 tw-h-[100vh] tw-overflow-y-auto">
      <div class="tw-flex tw-justify-between tw-items-center tw-mb-4 tw-bg-white tw-pb-2">
        <div class="tw-w-12"></div>

        <p class="tw-font-bold tw-text-lg">{{ t('appointmentrequest.select_branch_title') }}</p>
        <v-btn icon variant="text" @click="close" class="tw-text-base tw-text-red-500">
          <van-icon name="cross" size="20" />
        </v-btn>
      </div>

      <div v-for="group in groupingBranch" :key="group.name" class="tw-mb-6">
        <template v-if="group.branches.length > 0">
          <div class="tw-mb-3">
            <p class="text-primary2 tw-text-base tw-text-[20px]">{{ group.name }}</p>
          </div>
          <div class="tw-border-2 tw-rounded-3xl tw-p-3" :style="{ borderColor: primary2 }">
            <div class="tw-grid tw-grid-cols-2 tw-gap-3">
              <v-btn
                v-for="branch in group.branches"
                :key="branch.uuid"
                :style="{ borderColor: primary2 }"
                class="tw-rounded-full tw-text-base tw-font-bold"
                :class="{ 'bg-primary2 tw-text-white': selected === branch.uuid }"
                variant="outlined"
                @click="selectBranch(branch)"
              >
                {{ getBranchDisplayNamei18n(branch, appStore.language) }}
              </v-btn>
            </div>
          </div>
        </template>
      </div>

      <!-- <Debug class="tw-text-sm">
        <p>--------------</p>
        <div>
          <p>Current locations</p>
          <p>latitude: {{ coords.latitude }}</p>
          <p>longitude: {{ coords.longitude }}</p>
          <p class="tw-text-red-500" v-if="geoError">error: {{ geoError?.message }}</p>
        </div>
        <p>--------------</p>
        <div class="tw-text-orange-500">
          <p>Selected Branch: {{ selectedText || '-' }}</p>
          <p>latitude: {{ selectedCoords?.latitude || '-' }}</p>
          <p>longitude: {{ selectedCoords?.longitude || '-' }}</p>
        </div>
        <p>--------------</p>
      </Debug> -->

      <div class="tw-sticky tw-bottom-0 tw-bg-white tw-pt-4 tw-pb-2">
        <v-btn
          block
          class="bg-primary2 tw-rounded-full tw-text-white tw-text-lg tw-py-6"
          :disabled="!selected"
          @click="confirm"
        >
          {{ t('submit') }}
        </v-btn>
      </div>
    </div>
  </van-popup>
</template>

<style scoped>
.border-primary {
  border: 2px solid var(--v-theme-primary2);
}

::v-deep .bg-primary2 {
  color: white !important;
}
</style>
